import { createContext, useContext, useState, useEffect } from "react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import "./i18n"

type LanguageContextType = {
  currentLanguage: string
  changeLanguage: (lng: string) => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { i18n } = useTranslation()
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || "ua")

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
  }

  useEffect(() => {
    // Set default language to 'ua' if no preference is stored
    try {
      const storedLanguage = localStorage.getItem("i18nextLng")
      if (!storedLanguage) {
        i18n.changeLanguage("ua")
      }
    } catch (_) {
      // Ignore storage access errors and rely on i18n fallback
    }
  }, [i18n])

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng)
      // Save language preference to localStorage
      try {
        if (typeof localStorage?.setItem === "function") {
          localStorage.setItem("i18nextLng", lng)
        }
      } catch (_) {
        // Ignore storage access errors and rely on i18n state
      }
    }

    i18n.on("languageChanged", handleLanguageChanged)

    return () => {
      i18n.off("languageChanged", handleLanguageChanged)
    }
  }, [i18n])

  const value = {
    currentLanguage,
    changeLanguage,
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export default LanguageProvider
